import { calendarDaysBetween, fullMonthsBetween, toCalendarDate } from './calendarDate';

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Valor y unidad para `Intl.RelativeTimeFormat` entre `timestamp` y `now` (negativo = pasado).
 *
 * Nunca exagera la distancia. Bajo 24 h trunca el tiempo transcurrido; desde ahí cuenta días de
 * calendario en `timeZone` (la del navegador si es `undefined`), meses completos y años de 12
 * meses. Lanza `RangeError` si alguno de los dos instantes no es finito.
 */
export const getRelativeTimeOptions = (
  timestamp: number,
  now: number,
  timeZone?: string,
): readonly [number, Intl.RelativeTimeFormatUnit] => {
  const sign = timestamp < now ? -1 : 1;
  const elapsed = Math.abs(timestamp - now);

  if (elapsed < MINUTE) return [sign * Math.trunc(elapsed / SECOND), 'second'];
  if (elapsed < HOUR) return [sign * Math.trunc(elapsed / MINUTE), 'minute'];

  const earlier = toCalendarDate(Math.min(timestamp, now), timeZone);
  const later = toCalendarDate(Math.max(timestamp, now), timeZone);
  const days = calendarDaysBetween(earlier, later);
  // 24 h pueden caer en el mismo día de calendario cuando el día dura 25 h (cambio de hora).
  if (elapsed < DAY || days === 0) return [sign * Math.trunc(elapsed / HOUR), 'hour'];

  const months = fullMonthsBetween(earlier, later);
  if (months < 1) return [sign * days, 'day'];
  if (months < 12) return [sign * months, 'month'];
  return [sign * Math.trunc(months / 12), 'year'];
};
